"use client";
import { useUiControlLabels } from "@/components/i18n/ui-control-labels-context"


import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  FeedbackStatusConfirmDialog,
  type FeedbackStatusAction,
} from "@/components/ai/feedback/feedback-status-confirm-dialog";
import {
  Loader2,
  MessageSquare,
  RefreshCw,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  PlayCircle,
  CheckCheck,
  Clock,
  Phone,
  Pencil,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"




const formatCallId = (id: string | number | undefined): { short: string; full: string } => {
  if (!id) return { short: "-", full: "-" };
  const str = String(id);
  if (str.length <= 5) return { short: str, full: str };
  return { short: `..${str.slice(-3)}`, full: str };
};

type FeedbackStatus = "pending" | "in_progress" | "resolved";

type FeedbackItem = {
  id: string;
  reference_id: string;
  feedback_text: string | null;
  status: FeedbackStatus;
  created_at: string | null;
  created_by: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  agent_id: string | null;
  // Call data loaded separately
  call_id?: number;
  agent_number?: string;
  external_number?: string;
  call_status?: string;
  agent_name?: string;
};

interface VoiceCallFeedbackTableProps {
  onViewDetails?: (conversationId: string) => void;
  voiceAgentId?: string;
}

export function VoiceCallFeedbackTable({ onViewDetails, voiceAgentId }: VoiceCallFeedbackTableProps) {
  const {locale: uiFormattingLocale} = useUiControlLabels()


  const tUi = useTranslations("Calls.CallsVoiceCallFeedbackTable")
  const tCommonUi = useTranslations("Common")

  const { fetchWithAuth } = useAuth();
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all_with_feedback");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    feedbackId: string;
    newStatus: FeedbackStatusAction;
    feedbackText?: string;
  } | null>(null);
  const [editDialog, setEditDialog] = useState<{
    feedbackId: string;
    feedbackText: string;
  } | null>(null);
  const [editText, setEditText] = useState("");
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const pageSize = 20;

  const loadItems = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
        agent_type: "voice_agent",
      });

      if (statusFilter !== "all" && statusFilter !== "all_with_feedback") {
        params.set("status", statusFilter);
      }

      // Filter by voice agent if voiceAgentId is provided
      if (voiceAgentId) {
        params.set("agent_id", voiceAgentId);
      }

      const res = await fetchWithAuth(`/api/ai/feedback?${params}`);
      if (!res.ok) throw new Error("Failed to load feedback");

      const data = await res.json();

      // Enhance items with call data
      const feedbackItems = data.items || [];

      // Load call details for each feedback item
      const enhancedItems = await Promise.all(
        feedbackItems.map(async (item: FeedbackItem) => {
          try {
            // Get call by conversation_id using search parameter
            const callRes = await fetchWithAuth(
              `/api/calls?search=${encodeURIComponent(item.reference_id)}&pageSize=1&source=voice_agent`
            );
            const callData = await callRes.json();
            const call = callData.calls?.[0];

            return {
              ...item,
              call_id: call?.voiceAgentData?.id || call?.originalId,
              agent_number: call?.calledNumber,
              external_number: call?.callerNumber || call?.calledNumber,
              call_status: call?.status,
              agent_name: call?.voiceAgent?.name,
            };
          } catch {
            return item;
          }
        })
      );

      setItems(enhancedItems);
      setTotal(data.total || 0);
    } catch (error) {
      toast.error(tUi("failedToLoadFeedbackList"));
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth, page, statusFilter, tUi, voiceAgentId]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleUpdateStatus = async (feedbackId: string, newStatus: "in_progress" | "resolved") => {
    try {
      setUpdatingId(feedbackId);
      const res = await fetchWithAuth(`/api/ai/feedback/${feedbackId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!res.ok) throw new Error("Failed to update status");

      toast.success(newStatus === "resolved" ? "Marked as resolved" : "Marked as in progress");

      // Update local state
      setItems((prev) =>
        prev.map((item) =>
          item.id === feedbackId ? { ...item, status: newStatus } : item
        )
      );
    } catch (_error) {
      toast.error(tUi("failedToUpdateStatus"));
    } finally {
      setUpdatingId(null);
    }
  };

  const handleUpdateFeedbackText = async () => {
    if (!editDialog) return;

    try {
      setIsSavingEdit(true);
      const res = await fetchWithAuth(`/api/ai/feedback/${editDialog.feedbackId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ feedback_text: editText }),
      });

      if (!res.ok) throw new Error("Failed to update feedback");

      toast.success(tUi("feedbackUpdated"));

      // Update local state
      setItems((prev) =>
        prev.map((item) =>
          item.id === editDialog.feedbackId ? { ...item, feedback_text: editText } : item
        )
      );
      setEditDialog(null);
    } catch (_error) {
      toast.error(tUi("failedToUpdateFeedback"));
    } finally {
      setIsSavingEdit(false);
    }
  };

  const openEditDialog = (feedbackId: string, feedbackText: string) => {
    setEditDialog({ feedbackId, feedbackText });
    setEditText(feedbackText);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString(uiFormattingLocale, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status: FeedbackStatus) => {
    switch (status) {
      case "pending":
        return (
          <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
            <Clock className="w-3 h-3 mr-1" />
            <span><span key={tUi("pending")}>{tUi("pending")}</span></span>
          </Badge>
        );
      case "in_progress":
        return (
          <Badge className="bg-blue-100 text-blue-800 border-blue-200">
            <PlayCircle className="w-3 h-3 mr-1" />
            <span><span key={tUi("inProgress")}>{tUi("inProgress")}</span></span>
          </Badge>
        );
      case "resolved":
        return (
          <Badge className="bg-green-100 text-green-800 border-green-200">
            <CheckCheck className="w-3 h-3 mr-1" />
            <span><span key={tUi("resolved")}>{tUi("resolved")}</span></span>
          </Badge>
        );
      default:
        return null;
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <TooltipProvider>
    <div className="space-y-4">
      {/* Header with filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-orange-600" />
          <h3 className="font-medium"><span key={tUi("voiceCallFeedback")}>{tUi("voiceCallFeedback")}</span></h3>
          <Badge variant="secondary">{total}</Badge>
        </div>
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="min-w-0 flex-1 sm:w-[180px]">
              <SelectValue placeholder={tUi("filterByStatus")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all_with_feedback"><span key={tUi("allWithFeedback")}>{tUi("allWithFeedback")}</span></SelectItem>
              <SelectItem value="pending"><span key={tUi("pending")}>{tUi("pending")}</span></SelectItem>
              <SelectItem value="in_progress"><span key={tUi("inProgress")}>{tUi("inProgress")}</span></SelectItem>
              <SelectItem value="resolved"><span key={tUi("resolved")}>{tUi("resolved")}</span></SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={loadItems} disabled={isLoading} className="shrink-0">
            <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p><span key={tUi("noFeedbackFound")}>{tUi("noFeedbackFound")}</span></p>
        </div>
      ) : (
        <>
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]"><span key={tUi("status")}>{tUi("status")}</span></TableHead>
                  <TableHead className="w-[80px]"><span key={tUi("callID")}>{tUi("callID")}</span></TableHead>
                  <TableHead><span key={tUi("agent")}>{tUi("agent")}</span></TableHead>
                  <TableHead><span key={tUi("phoneNumbers")}>{tUi("phoneNumbers")}</span></TableHead>
                  <TableHead className="max-w-[300px]"><span key={tUi("feedback")}>{tUi("feedback")}</span></TableHead>
                  <TableHead className="w-[120px]"><span key={tUi("created")}>{tUi("created")}</span></TableHead>
                  <TableHead className="w-[150px]"><span key={tUi("actions")}>{tUi("actions")}</span></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{getStatusBadge(item.status)}</TableCell>
                    <TableCell>
                      {item.call_id ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-sm font-medium cursor-help">
                              #{formatCallId(item.call_id).short}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <span>#</span>{formatCallId(item.call_id).full}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{item.agent_name || "-"}</span>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-1 text-sm">
                          <Phone className="w-3 h-3 text-gray-400" />
                          <span className="text-gray-600">
                            {item.external_number || "-"}
                          </span>
                        </div>
                        {item.agent_number && (
                          <div className="text-xs text-gray-500">
                            <span><span key={tUi("agent2")}>{tUi("agent2")}</span></span> {item.agent_number}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-gray-700 line-clamp-2">
                        {item.feedback_text || "-"}
                      </p>
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">
                      {formatDate(item.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {item.status === "pending" && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2"
                              onClick={() => openEditDialog(item.id, item.feedback_text || "")}
                              title={tUi("editFeedback")}
                            >
                              <Pencil className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2"
                              onClick={() => setConfirmDialog({
                                feedbackId: item.id,
                                newStatus: "in_progress",
                                feedbackText: item.feedback_text || undefined,
                              })}
                              disabled={updatingId === item.id}
                              title={tUi("takeInProgress")}
                            >
                              {updatingId === item.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <PlayCircle className="w-3 h-3" />
                              )}
                            </Button>
                          </>
                        )}
                        {item.status && item.status !== "resolved" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-green-600 hover:text-green-700"
                            onClick={() => setConfirmDialog({
                              feedbackId: item.id,
                              newStatus: "resolved",
                              feedbackText: item.feedback_text || undefined,
                            })}
                            disabled={updatingId === item.id}
                            title={tUi("markAsResolved")}
                          >
                            {updatingId === item.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <CheckCheck className="w-3 h-3" />
                            )}
                          </Button>
                        )}
                        {onViewDetails && item.reference_id && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2"
                            onClick={() => onViewDetails(item.reference_id)}
                          >
                            <ExternalLink className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                <span><span key={tUi("showing")}>{tUi("showing")}</span></span> {(page - 1) * pageSize + 1} <span>-</span> {Math.min(page * pageSize, total)} <span><span key={tUi("of")}>{tUi("of")}</span></span>{" "}
                {total}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p - 1)}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm"><TranslationText>{tUi.rich("pageValue0OfValue1", { value0: String(page), value1: String(totalPages) })}</TranslationText></span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= totalPages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <FeedbackStatusConfirmDialog
        open={!!confirmDialog}
        onOpenChange={(open) => !open && setConfirmDialog(null)}
        action={confirmDialog?.newStatus || null}
        feedbackText={confirmDialog?.feedbackText}
        onConfirm={() => {
          if (confirmDialog) {
            handleUpdateStatus(confirmDialog.feedbackId, confirmDialog.newStatus);
            setConfirmDialog(null);
          }
        }}
      />

      {/* Edit Feedback Dialog */}
      <Dialog open={!!editDialog} onOpenChange={(open) => !open && setEditDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle><span key={tUi("editFeedback2")}>{tUi("editFeedback2")}</span></DialogTitle>
            <DialogDescription>
              <span key={tUi("updateTheFeedbackTextYouCanOnlyEdit")}>{tUi("updateTheFeedbackTextYouCanOnlyEdit")}</span></DialogDescription>
          </DialogHeader>
          <Textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            placeholder={tUi("enterFeedbackText")}
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(null)}>
              <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
            <Button onClick={handleUpdateFeedbackText} disabled={isSavingEdit}>
              {isSavingEdit && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <span><span key={tUi("save")}>{tUi("save")}</span></span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </TooltipProvider>
  );
}
